import { createContext, useCallback, useContext, useSyncExternalStore } from "react";
import type { Locale } from "@/types";

export const SITE_LOCALE_STORAGE_KEY = "zesrin.site-locale";
const listeners = new Set<() => void>();
let currentLocale: Locale | undefined;

export const SiteLocaleContext = createContext<{ locale: Locale; setLocale(locale: Locale): void } | null>(null);

export function parseSiteLocale(value: string | null): Locale | undefined {
  if (value === "zh" || value === "zh-CN") return "zh";
  return value === "en" ? "en" : undefined;
}

function isLocale(value: string | null): value is Locale {
  return value === "en" || value === "zh";
}

export function readSiteLocale(fallback: Locale = "en"): Locale {
  if (typeof window === "undefined") return fallback;
  try {
    const stored = window.localStorage.getItem(SITE_LOCALE_STORAGE_KEY);
    currentLocale = isLocale(stored) ? stored : fallback;
  } catch {
    currentLocale ??= fallback;
  }
  return currentLocale ?? fallback;
}

export function persistSiteLocale(locale: Locale) {
  currentLocale = locale;
  try {
    window.localStorage.setItem(SITE_LOCALE_STORAGE_KEY, locale);
  } catch {
    // Storage can be unavailable in privacy-restricted browsers.
  }
  document.documentElement.lang = locale === "zh" ? "zh-CN" : "en";
  listeners.forEach((listener) => listener());
}

export function useSiteLocale(fallback: Locale = "en") {
  const route = useContext(SiteLocaleContext);
  const storedLocale = useSyncExternalStore(
    (listener) => { listeners.add(listener); return () => listeners.delete(listener); },
    () => readSiteLocale(fallback),
    () => fallback,
  );
  const locale = route?.locale ?? storedLocale;

  const toggleLocale = useCallback(() => {
    const next = locale === "en" ? "zh" : "en";
    persistSiteLocale(next);
    route?.setLocale(next);
  }, [locale, route]);

  const setLocale = useCallback((next: Locale) => {
    persistSiteLocale(next);
    route?.setLocale(next);
  }, [route]);

  return { locale, setLocale, toggleLocale };
}
